// DataTable.jsx
import { useState } from 'react';
import { motion } from 'framer-motion';
import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight
} from 'lucide-react';
import Button from './Button';
import Loading from './Loading';

const SortIcon = ({ direction, active }) => {
  // direction: 'asc' | 'desc' | null
  const common = {
    width: 16,
    height: 16,
    stroke: active ? 'var(--primary-600)' : 'var(--gray-400)'
  };

  if (!direction) {
    // neutrally-styled up/down icon
    return (
      <svg viewBox="0 0 24 24" width={common.width} height={common.height} fill="none" stroke={common.stroke}>
        <path strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" d="M7 16V4m0 0L3 8m4-4l4 4M17 8v12m0 0l4-4m-4 4l-4-4" />
      </svg>
    );
  }

  if (direction === 'asc') {
    return (
      <svg viewBox="0 0 24 24" width={common.width} height={common.height} fill="none" stroke={common.stroke}>
        <path strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" d="M5 15l7-7 7 7" />
      </svg>
    );
  }

  return (
    <svg viewBox="0 0 24 24" width={common.width} height={common.height} fill="none" stroke={common.stroke}>
      <path strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
    </svg>
  );
};

const DataTable = ({
  data = [],
  columns = [],
  loading = false,
  emptyMessage = 'No data available',
  pagination = null,
  onPageChange = null,
  sortable = true,
  className = ''
}) => {
  const [sortConfig, setSortConfig] = useState({ key: null, direction: null });

  const handleSort = key => {
    if (!sortable) return;

    let direction = 'asc';
    if (sortConfig.key === key) {
      direction = sortConfig.direction === 'asc' ? 'desc' : 'asc';
    }
    setSortConfig({ key, direction });
  };

  // shallow row value accessor (supports nested keys like 'user.name' if provided)
  const getValue = (row, key) => {
    if (!key) return undefined;
    if (key.includes('.')) {
      return key.split('.').reduce((acc, part) => (acc ? acc[part] : undefined), row);
    }
    return row[key];
  };

  const sortedData = (() => {
    if (!sortConfig.key) return [...data];
    const arr = [...data];
    const key = sortConfig.key;
    arr.sort((a, b) => {
      const aValue = getValue(a, key);
      const bValue = getValue(b, key);

      // handle undefined/null gracefully
      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return sortConfig.direction === 'asc' ? -1 : 1;
      if (bValue == null) return sortConfig.direction === 'asc' ? 1 : -1;

      // numeric vs string comparison
      const aNum = typeof aValue === 'number' ? aValue : parseFloat(aValue);
      const bNum = typeof bValue === 'number' ? bValue : parseFloat(bValue);
      if (!isNaN(aNum) && !isNaN(bNum)) {
        return sortConfig.direction === 'asc' ? aNum - bNum : bNum - aNum;
      }

      const aStr = String(aValue).toLowerCase();
      const bStr = String(bValue).toLowerCase();
      if (aStr < bStr) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aStr > bStr) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
    return arr;
  })();

  // Inline styles (match the inline-style pattern used across your app)
  const styles = {
    container: {
      background: '#fff',
      borderRadius: 12,
      boxShadow: '0 10px 30px rgba(2,6,23,0.04)',
      border: '1px solid #eef2f6',
      overflow: 'hidden'
    },
    tableWrapper: { overflowX: 'auto' },
    thead: { background: 'var(--gray-50, #f9fafb)', borderBottom: '1px solid #e9eef4' },
    th: (w, clickable) => ({
      padding: '16px 20px',
      textAlign: 'left',
      fontSize: 12,
      fontWeight: 600,
      color: 'var(--gray-600)',
      textTransform: 'uppercase',
      letterSpacing: '0.02em',
      cursor: clickable ? 'pointer' : 'default',
      userSelect: clickable ? 'none' : 'auto',
      width: w || 'auto'
    }),
    tbody: { borderTop: '1px solid transparent' },
    tr: { transition: 'background-color 150ms, transform 120ms' },
    trHover: { background: '#fbfdff' },
    td: { padding: '12px 20px', whitespace: 'nowrap', fontSize: 14, color: 'var(--gray-800)' },
    emptyState: {
      padding: '48px 24px',
      textAlign: 'center',
      display: 'flex',
      flexDirection: 'column',
      gap: 12,
      alignItems: 'center'
    },
    iconBubble: { width: 64, height: 64, borderRadius: 9999, background: '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center' },
    paginationBar: { padding: '12px 16px', borderTop: '1px solid #eef2f6', background: 'var(--gray-50,#fbfdff)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={{ padding: 32, textAlign: 'center' }}>
          <Loading size="lg" text="Loading data..." />
        </div>
      </div>
    );
  }

  return (
    <div style={{ ...styles.container }} className={className}>
      <div style={styles.tableWrapper}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead style={styles.thead}>
            <tr>
              {columns.map((column, index) => {
                const clickable = sortable && column.sortable !== false && !!column.key;
                const isActive = sortConfig.key === column.key;
                return (
                  <th
                    key={column.key ?? index}
                    style={styles.th(column.width, clickable)}
                    onClick={() => clickable && handleSort(column.key)}
                    role={clickable ? 'button' : undefined}
                    tabIndex={clickable ? 0 : undefined}
                    onKeyDown={e => {
                      if (clickable && (e.key === 'Enter' || e.key === ' ')) {
                        e.preventDefault();
                        handleSort(column.key);
                      }
                    }}
                    aria-sort={isActive ? (sortConfig.direction === 'asc' ? 'ascending' : 'descending') : 'none'}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span>{column.label}</span>
                      {clickable && <SortIcon direction={isActive ? sortConfig.direction : null} active={isActive} />}
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>

          <tbody style={styles.tbody}>
            {sortedData.length > 0 ? (
              sortedData.map((row, rowIndex) => (
                <motion.tr
                  key={row.id ?? rowIndex}
                  style={styles.tr}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.18, delay: rowIndex * 0.01 }}
                  whileHover={{ backgroundColor: '#fbfdff' }}
                >
                  {columns.map((column, colIndex) => (
                    <td key={column.key ?? colIndex} style={{ padding: '12px 20px', fontSize: 14, color: 'var(--gray-800)' }}>
                      {column.render ? column.render(row) : getValue(row, column.key)}
                    </td>
                  ))}
                </motion.tr>
              ))
            ) : (
              <tr>
                <td colSpan={columns.length} style={{ padding: 32 }}>
                  <div style={styles.emptyState}>
                    <div style={styles.iconBubble}>
                      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" style={{ color: 'var(--gray-400)' }}>
                        <path strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" d="M9 12h6M9 16h6M3 7a2 2 0 012-2h10l4 4v9a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
                      </svg>
                    </div>

                    <div>
                      <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: 'var(--gray-900)' }}>{emptyMessage}</h3>
                      <p style={{ margin: '8px 0 0', color: 'var(--gray-500)' }}>Data will appear here when available.</p>
                    </div>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {pagination && (
        <div style={styles.paginationBar}>
          <div style={{ fontSize: 10, color: 'var(--gray-600)' }}>
                        Showing {(pagination.currentPage - 1) * pagination.pageSize + 1} to{' '}
            {Math.min(pagination.currentPage * pagination.pageSize, pagination.totalCount)} of {pagination.totalCount} results
          </div>

          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <Button
              variant="outline"
              size="sm"
              leftIcon={<ChevronsLeft />}
              onClick={() => onPageChange && onPageChange(1)}
              disabled={pagination.currentPage === 1}
            >
              {/* First */}
            </Button>

            <Button
              variant="outline"
              size="sm"
              leftIcon={<ChevronLeft />}
              onClick={() => onPageChange && onPageChange(Math.max(1, pagination.currentPage - 1))}
              disabled={pagination.currentPage === 1}
            >
              {/* Previous */}
            </Button>

            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              {Array.from({ length: Math.min(5, pagination.totalPages) }).map((_, i) => {
                let pageNumber;
                if (pagination.totalPages <= 5) {
                  pageNumber = i + 1;
                } else if (pagination.currentPage <= 3) {
                  pageNumber = i + 1;
                } else if (pagination.currentPage >= pagination.totalPages - 2) {
                  pageNumber = pagination.totalPages - 4 + i;
                } else {
                  pageNumber = pagination.currentPage - 2 + i;
                }

                // skip invalid page numbers (can happen when totalPages < 5)
                if (pageNumber < 1 || pageNumber > pagination.totalPages) return null;

                return (
                  <Button
                    key={pageNumber}
                    variant={pageNumber === pagination.currentPage ? 'primary' : 'ghost'}
                    size="sm"
                    onClick={() => onPageChange && onPageChange(pageNumber)}
                    style={{ minWidth: 40, textAlign: 'center' }}
                  >
                    {pageNumber}
                  </Button>
                );
              })}
            </div>

            <Button
              variant="outline"
              size="sm"
              rightIcon={<ChevronRight />}
              onClick={() => onPageChange && onPageChange(Math.min(pagination.totalPages, pagination.currentPage + 1))}
              disabled={pagination.currentPage === pagination.totalPages}
            >
              {/* Next */}
            </Button>

            <Button
              variant="outline"
              size="sm"
              rightIcon={<ChevronsRight />}
              onClick={() => onPageChange && onPageChange(pagination.totalPages)}
              disabled={pagination.currentPage === pagination.totalPages}
            >
              {/* Last */}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DataTable;
